import { useState } from 'react';
import { Video, AudioLines, FileText, File, Image as ImageIcon } from 'lucide-react';
import { cn } from '@/lib/cn';
import type { NormalizedItem } from './types';

interface ThumbnailProps {
  item: NormalizedItem;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  onClick?: () => void;
}

const sizeClasses = {
  sm: 'h-10 w-10',
  md: 'h-20 w-20',
  lg: 'h-32 w-full',
};

const iconSizes = {
  sm: 'h-5 w-5',
  md: 'h-8 w-8',
  lg: 'h-12 w-12',
};

export function Thumbnail({ item, size = 'md', className, onClick }: ThumbnailProps) {
  const [imgError, setImgError] = useState(false);
  const thumbnailSrc = item.thumbnailUrl || item.snapshotUrl;
  const hasImage = thumbnailSrc && !imgError;

  if (hasImage) {
    return (
      <div
        className={cn(
          'flex items-center justify-center overflow-hidden rounded bg-secondary',
          sizeClasses[size],
          onClick && 'cursor-pointer',
          className,
        )}
        onClick={onClick}
      >
        <img
          src={thumbnailSrc}
          alt={item.title}
          className="h-full w-full object-cover"
          onError={() => setImgError(true)}
        />
      </div>
    );
  }

  const Icon = getTypeIcon(item.type || item.itemtype);
  const iconColor = getTypeColor(item.type || item.itemtype);

  return (
    <div
      className={cn(
        'flex items-center justify-center rounded bg-secondary',
        sizeClasses[size],
        onClick && 'cursor-pointer',
        className,
      )}
      onClick={onClick}
    >
      <Icon className={cn(iconSizes[size], iconColor)} />
    </div>
  );
}

function getTypeIcon(type?: string) {
  switch (type) {
    case 'video': return Video;
    case 'audio': return AudioLines;
    case 'image': return ImageIcon;
    case 'doc':
    case 'document':
    case 'pdf': return FileText;
    default: return File;
  }
}

function getTypeColor(type?: string) {
  switch (type) {
    case 'video': return 'text-blue-500';
    case 'audio': return 'text-purple-500';
    case 'image': return 'text-emerald-500';
    case 'doc':
    case 'document':
    case 'pdf': return 'text-orange-500';
    default: return 'text-muted-foreground';
  }
}
